import { useNavigate } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  Calendar,
  CheckSquare,
  DollarSign,
  Lightbulb,
  PieChart,
  Scale,
  TrendingUp,
  UserCheck,
  Users,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { COLOR_PALETTE, type ReportConfig } from "../types";

const ICON_MAP: Record<string, LucideIcon> = {
  Activity,
  AlertCircle,
  Calendar,
  CheckSquare,
  DollarSign,
  Lightbulb,
  PieChart,
  Scale,
  TrendingUp,
  UserCheck,
  Users,
  Zap,
};

const CATEGORY_LABEL: Record<string, string> = {
  projects: "Projects",
  finance: "Finance",
  people: "People",
  work: "Work",
};

const CATEGORY_PALETTE: Record<
  string,
  { bg: string; fg: string }
> = {
  projects: { bg: "hsl(210,55%,92%)", fg: "hsl(210,100%,28%)" },
  finance: { bg: "hsl(142,55%,92%)", fg: "hsl(142,72%,28%)" },
  people: { bg: "hsl(270,55%,92%)", fg: "hsl(270,75%,32%)" },
  work: { bg: "hsl(38,92%,92%)", fg: "hsl(38,92%,30%)" },
};

interface ReportCardProps {
  report: ReportConfig;
}

export function ReportCard({ report }: ReportCardProps) {
  const navigate = useNavigate();
  const Icon = ICON_MAP[report.icon] ?? Activity;
  const color = COLOR_PALETTE[report.color];
  const cat = CATEGORY_PALETTE[report.category];

  return (
    <button
      type="button"
      onClick={() => navigate(`/reports/${report.id}`)}
      className="group flex w-full flex-col items-stretch rounded-xl border bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md"
      style={{ borderColor: "hsl(210,30%,85%)" }}
    >
      <div
        className="flex h-12 w-12 items-center justify-center rounded-full"
        style={{ background: color.bg }}
      >
        <Icon className="h-6 w-6" strokeWidth={1.6} style={{ color: color.fg }} />
      </div>

      <div className="mt-3 text-[15px] font-bold leading-snug text-foreground">
        {report.title}
      </div>
      <div
        className="mt-1 text-[13px] text-muted-foreground"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {report.description}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span
          className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
          style={{ background: cat.bg, color: cat.fg }}
        >
          {CATEGORY_LABEL[report.category]}
        </span>
        <span className="text-[12px] font-medium text-[hsl(210,100%,40%)] transition-transform group-hover:translate-x-0.5">
          Run Report →
        </span>
      </div>
    </button>
  );
}
